import { priceFormatter, priceFormatterNoCents } from '../helpers/formatters';
import {
  Operation,
  OperationChain,
  OperationChainLink,
  Scenario,
} from '@/types';
import { toLink } from './stringUtils';
import { toHtmlTable } from './objectUtils';
import { copyToClipboard } from './clipboardUtils';

type ChainOverview = {
  Profit: string;
  Volume: string;
  'Profit/Dth': string;
  'Total Transport Cost': string;
};

type ChainEndpoint = {
  Type: string;
  Pipeline: string;
  Point: string | undefined;
  'Loc ID': string | undefined;
  Operation: string;
  Market: string | undefined;
  Price: string;
};

type ChainOperation = {
  Receipt: string;
  Delivery: string;
  Pipeline: string;
  'Received Volume': string;
  'Delivered Volume': string;
  Priority: number | string;
  Contract: string;
  'Variable Cost': string;
  'Fuel Cost': string;
  'Transport Cost': string;
};

type ChainConstraint = {
  'Constraint Type': string;
  Description: string;
  // 'Utilization Rate': string;
  'Max Capacity': string;
  Direction: string;
  'Cut Priority': number | string;
};

type ChainSummary = {
  overview: ChainOverview;
  endpoints: ChainEndpoint[];
  operations: ChainOperation[];
  constraints: ChainConstraint[];
};

export const getChainSummary = (
  scenario: Scenario,
  chain: OperationChain,
): ChainSummary => {
  const chainOperations = chain.chain_links
    .filter((chain_link) => chain_link.has_flow == true)
    .map((chain_link) => {
      const operation = scenario.operations.find(
        (op) => op.id === chain_link.operation_id,
      );
      return {
        ...operation,
        chain_link,
      } as Operation & { chain_link: OperationChainLink };
    });

  const receiptOp = chainOperations[0];
  const deliveryOp = chainOperations[chainOperations.length - 1];

  const receiptMarket =
    scenario.markets.find(
      (market) =>
        market.point.toString() === receiptOp.receipt_point?.id.toString() &&
        market.buy_sell !== 'SELL',
    ) ||
    scenario.trades.find((trade) => {
      return (
        trade.point.toString() === receiptOp.receipt_point?.id.toString() &&
        trade.buy_sell !== 'SELL'
      );
    });

  const deliveryMarket =
    scenario.markets.find(
      (market) =>
        market.point.toString() === deliveryOp.delivery_point?.id.toString() &&
        market.buy_sell !== 'BUY',
    ) ||
    scenario.trades.find((trade) => {
      return (
        trade.point.toString() === deliveryOp.delivery_point?.id.toString() &&
        trade.buy_sell !== 'BUY'
      );
    });

  let overallTransportCost = 0;

  const endpoints: ChainEndpoint[] = [
    {
      Type: 'Receipt',
      Pipeline: receiptOp.tsp_name,
      Point: receiptOp.receipt_point?.name,
      'Loc ID': receiptOp.receipt_point?.loc_id,
      Operation: 'Buy',
      Market: receiptMarket?.description,
      Price: priceFormatter.format(receiptMarket?.price ?? 0),
    },
    {
      Type: 'Delivery',
      Pipeline: deliveryOp.tsp_name,
      Point: deliveryOp.delivery_point?.name,
      'Loc ID': deliveryOp.delivery_point?.loc_id,
      Operation: 'Sell',
      Market: deliveryMarket?.description,
      Price: priceFormatter.format(deliveryMarket?.price ?? 0),
    },
  ];
  const operations: ChainOperation[] = chainOperations.map((op) => {
    const fuelCost = chain.fuel_makeup_price * ((op.fuel_factor ?? 1) - 1);
    const transportCost = Number(op.transport_cost_per_dth ?? 0) + fuelCost;
    overallTransportCost += transportCost;
    return {
      Receipt: `${op.receipt_point?.loc_id} - ${op.receipt_point?.name} - ${op.receipt_point?.receipt_zone_name}`,
      Delivery: `${op.delivery_point?.loc_id} - ${op.delivery_point?.name} - ${op.delivery_point?.delivery_zone_name}`,
      Pipeline: op.tsp_name,
      'Received Volume': op.chain_link.received_dth.toFixed(0),
      'Delivered Volume': op.chain_link.delivered_dth.toFixed(0),
      Priority: op.transport_priority,
      Contract: `${op.contract_name} (${op.contract_k_id})`,
      'Variable Cost': priceFormatter.format(op.transport_cost_per_dth ?? 0),
      'Fuel Cost': priceFormatter.format(fuelCost),
      'Transport Cost': priceFormatter.format(transportCost),
    };
  });
  const segmentConstraints = chainOperations.flatMap((op) => {
    return op.constraints
      ?.filter((constraint) => constraint.type === 'SEGMENT')
      .map(({ id: constraintId }) => {
        const constraint = scenario.segment_constraints.find(
          (constraint) => Number(constraint.id) === constraintId,
        );
        if (!constraint) return null;
        return {
          'Constraint Type': 'Segment',
          Description: constraint.short_description ?? constraint.description,
          // Add this back when we start using constraint_factor
          // 'Utilization Rate': `${constraint.constraint_factor * 100}%`,
          'Max Capacity': `${constraint.max_volume} dth`,
          Direction: constraint.flow_direction,
          'Cut Priority': constraint.cuts_at_priority,
        };
      })
      .filter((item) => item !== null);
  });
  const pointConstraints: ChainConstraint[] = chainOperations.flatMap((op) => {
    return op.constraints
      .filter((constraint) => constraint.type === 'POINT')
      .map(({ id: constraintId }) => {
        const constraint = scenario.point_constraints.find(
          (constraint) => Number(constraint.id) === constraintId,
        );
        if (!constraint) return null;
        return {
          'Constraint Type': 'Point',
          Description: constraint.short_description ?? constraint.description,
          // Add this back when we start using constraint_factor
          // 'Utilization Rate': `${constraint.constraint_factor * 100}%`,
          'Max Capacity': `${constraint.max_volume} dth`,
          Direction: constraint.flow_direction,
          'Cut Priority': constraint.cuts_at_priority,
        };
      })
      .filter((item) => item !== null);
  });
  const constraints: ChainConstraint[] = [
    ...pointConstraints,
    ...segmentConstraints,
  ];
  const overview: ChainOverview = {
    Profit: priceFormatterNoCents.format(Number(chain.profit)),
    Volume: chain.delivered_dth.toFixed(0),
    'Profit/Dth': priceFormatter.format(chain.profit / chain.delivered_dth),
    'Total Transport Cost': priceFormatter.format(overallTransportCost),
  };

  return {
    overview,
    endpoints,
    operations,
    constraints,
  };
};

export const copyChainToClipboard = (
  scenario: Scenario,
  chain: OperationChain,
) => {
  // const chain = scenario.operation_chains.find((chain) => chain.id === chainId);
  const summary = getChainSummary(scenario, chain);
  const scenarioLink = toLink(
    `${scenario.name}: Chain #${chain.id}`,
    `${window.location.origin}/scenarios/${scenario.id}?selectedChain=${chain.id}`,
  );
  const overviewHtml = toHtmlTable(
    [summary['overview']],
    `${chain.callout_list.join('<br/>')}<br/>${scenarioLink}`,
  );
  const endpointsHtml = toHtmlTable(summary['endpoints'], 'Endpoints');
  const operationsHtml = toHtmlTable(summary['operations'], 'Operations');
  const constraintsHtml = toHtmlTable(summary['constraints'], 'Constraints');
  copyToClipboard(
    `${overviewHtml}</br>${endpointsHtml}</br>${operationsHtml}</br>${constraintsHtml}`,
    'text/html',
  );
};

export const getUniqueChainTsps = (
  scenario: Scenario,
  chain: OperationChain,
): number[] => {
  const tsps = chain.operations.flatMap((op_id) => {
    const operation = scenario.operations.find((op) => op.id === op_id);
    return operation
      ? [operation.receipt_point.tsp_id, operation.delivery_point.tsp_id]
      : [];
  });
  const tspSet = new Set(tsps);
  const uniqueTsps = Array.from(tspSet);

  return uniqueTsps;
};
